import type { PositionedNode } from './chartLayout'
import { descendantIds, siblingsOf } from './seatMoves'
import type { SeatDetail } from './types'

export type DropHint = 'child' | 'invalid'

/**
 * What releasing the dragged card on top of another card would mean.
 *
 * Free placement made this a much smaller question than it used to be. A card
 * released in open space simply stays where it was let go, so there are no
 * sibling slots to resolve and no capture window to tune: the only gesture that
 * means anything beyond "put it here" is dropping a card ON another card, which
 * also makes it that card's report.
 *
 * A card is carried by the point it was grabbed at, so the pointer is tested
 * first — a tall card grabbed by its header hangs below the card being aimed at
 * — and the card's own centre second.
 */
export interface DropResolution {
  /** Card the drop indicator is painted on. */
  anchorSeatId: number
  hint: DropHint
  /** The new parent, set only for an accepted drop. */
  parentSeatId: number | null
  /** Index among the new parent's children, set only for an accepted drop. */
  position: number
  /** Set only for `invalid`: why this drop is refused. */
  reason?: string
}

export interface DropLabels {
  /** Already lowercased, e.g. "role" / "roles". */
  seat: string
  seats: string
}

export interface ResolveDropInput {
  /** Laid-out visible cards, in flow coordinates. */
  nodes: PositionedNode[]
  /** Every seat in the chart (with any optimistic move already applied). */
  seats: SeatDetail[]
  draggedSeatId: number
  /** The dragged card's rectangle, in flow coordinates. */
  card: { x: number; y: number; width: number; height: number }
  /** The pointer, in flow coordinates. */
  pointer: { x: number; y: number }
  labels: DropLabels
}

/** The card under the release point, or null for a drop into open space. */
export function resolveDrop(input: ResolveDropInput): DropResolution | null {
  const { nodes, seats, draggedSeatId, card, pointer, labels } = input
  const dragged = seats.find(s => s.id === draggedSeatId)
  if (!dragged) return null

  const targets = nodes.filter(n => n.seat.id !== draggedSeatId)
  if (targets.length === 0) return null

  const inside = (n: PositionedNode, p: { x: number; y: number }) =>
    p.x >= n.x && p.x <= n.x + n.width && p.y >= n.y && p.y <= n.y + n.height
  const center = { x: card.x + card.width / 2, y: card.y + card.height / 2 }
  const hovered = targets.find(n => inside(n, pointer)) || targets.find(n => inside(n, center))
  if (!hovered) return null

  const anchor = hovered.seat
  if (descendantIds(seats, draggedSeatId).has(anchor.id)) {
    return {
      anchorSeatId: anchor.id,
      hint: 'invalid',
      parentSeatId: null,
      position: 0,
      reason: `You can't move ${labels.seat} "${dragged.name}" inside its own branch.`,
    }
  }

  if (!anchor.can_edit) {
    return {
      anchorSeatId: anchor.id,
      hint: 'invalid',
      parentSeatId: null,
      position: 0,
      reason: `You don't have permission to add ${labels.seats} under "${anchor.name}".`,
    }
  }

  return {
    anchorSeatId: anchor.id,
    hint: 'child',
    parentSeatId: anchor.id,
    position: siblingsOf(seats, anchor.id).filter(s => s.id !== draggedSeatId).length,
  }
}
